import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Users, Trophy, Flame, UserPlus, Loader2, Zap, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";

export default function Squad() {
  const [board, setBoard] = useState([]);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);

  const loadBoard = () => api.get("/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  useEffect(() => { loadBoard(); }, []);

  const byXp = useMemo(() =>
    [...board].sort((a, b) => (b.xp - a.xp) || (b.best_streak - a.best_streak)).map((u, i) => ({ ...u, rank: i + 1 })),
    [board]);
  const byStreak = useMemo(() =>
    [...board].sort((a, b) => (b.best_streak - a.best_streak) || (b.xp - a.xp)).map((u, i) => ({ ...u, rank: i + 1 })),
    [board]);

  const addFriend = async () => {
    if (!query.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/friends", { query });
      toast.success(`Added ${data.friend.name} to your squad!`);
      setQuery("");
      loadBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add friend");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-8" data-testid="squad-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Compete & grow together
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Your Squad</h1>
        <p className="text-white mt-2">Add friends and see who's building the most momentum.</p>
      </div>

      {/* Add friend */}
      <div className="p-5 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl max-w-xl">
        <label className="block text-xs uppercase tracking-wider text-white mb-2">Add a friend</label>
        <div className="flex gap-2">
          <input
            data-testid="add-friend-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addFriend()}
            placeholder="Friend's email or name…"
            className="flex-1 h-11 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] text-white outline-none focus:border-emerald-400 text-sm"
          />
          <button data-testid="add-friend-button" onClick={addFriend} disabled={adding} className="h-11 px-4 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 text-black font-semibold flex items-center gap-2 disabled:opacity-60">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>

      {/* Two leaderboards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Board
          title="Top by XP"
          icon={<Zap className="w-5 h-5 text-emerald-400" />}
          items={byXp}
          mode="xp"
          testid="leaderboard-xp"
        />
        <Board
          title="Top Streaks"
          icon={<Flame className="w-5 h-5 text-orange-500" />}
          items={byStreak}
          mode="streak"
          testid="leaderboard-streak"
        />
      </div>
    </div>
  );
}

function Board({ title, icon, items, mode, testid }) {
  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl" data-testid={testid}>
      <div className="flex items-center gap-2 mb-5">
        <Trophy className="w-5 h-5 text-amber-400" />
        <h2 className="font-display text-lg font-bold">{title}</h2>
      </div>
      <div className="space-y-2">
        {items.map((u) => (
          <motion.div
            key={u.id}
            layout
            data-testid={`${testid}-row-${u.rank}`}
            className={`flex items-center gap-3 p-3 rounded-xl border ${u.is_me ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#242417]"}`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono-stat font-bold text-sm ${
              u.rank === 1 ? "bg-amber-400 text-black" : u.rank === 2 ? "bg-[#4d4b1a] text-black" : u.rank === 3 ? "bg-orange-700 text-white" : "bg-white/5 text-white"
            }`}>
              {u.rank === 1 ? <Crown className="w-4 h-4" /> : u.rank}
            </div>
            <Avatar
              userId={u.id}
              name={u.name}
              email={u.email}
              hasAvatar={u.has_avatar}
              version={u.avatar_version}
              className="w-9 h-9"
              textClassName="text-sm"
              rounded="rounded-full"
              testid={`${testid}-avatar-${u.rank}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">
                {u.name} {u.is_me && <span className="text-xs text-emerald-400">(you)</span>}
              </div>
              <div className="text-xs text-white">Lvl {u.level}</div>
            </div>
            <div className="text-right shrink-0">
              {mode === "streak" ? (
                <>
                  <div className="font-mono-stat font-bold text-orange-400 flex items-center gap-1 justify-end">
                    <Flame className="w-4 h-4" />{u.best_streak}
                  </div>
                  <div className="text-[10px] text-white uppercase">day streak</div>
                </>
              ) : (
                <>
                  <div className="font-mono-stat font-bold text-emerald-400 flex items-center gap-1 justify-end">
                    <Zap className="w-4 h-4" />{u.xp}
                  </div>
                  <div className="text-[10px] text-white uppercase">XP</div>
                </>
              )}
            </div>
          </motion.div>
        ))}
        {items.length <= 1 && (
          <p className="text-white text-sm text-center py-4">Add a friend to fill out this board.</p>
        )}
      </div>
    </div>
  );
}
